import type { ColumnFiltersState } from '@tanstack/react-table';
import type {
  DateRangePayload,
  DcaWorkspaceWellsQueryPayload,
  MinMaxPayload,
} from 'types/dca-workspace-wells-api/requests';

const TEXT_FILTER_MAP: Record<string, string> = {
  uwi: 'uwi_text',
  wellName: 'well_name',
  holeDirection: 'hole_direction',
  currentOperator: 'current_operator',
  srpLandingZone: 'srp_landing_zone',
  typeCurveFormation: 'type_curve_formation',
  typeCurveArea: 'type_curve_area',
  typeCurve: 'type_curve',
  proppantMeshSize: 'proppant_mesh_size',
  fluidSystem: 'fluid_system',
  createdByDisplay: 'created_by',
};

const RANGE_FILTER_MAP: Record<string, string> = {
  spudDate: 'spud_date',
  completionDate: 'completion_date',
  firstProductionDate: 'first_production_date',
  jobDate: 'job_date',
  createdAtDisplay: 'created_at',
  totalMonthsOnline: 'total_months_online',
  trueVerticalDepthFt: 'true_vertical_depth_ft',
  perforatedLateralLengthFt: 'perforated_lateral_length_ft',
  proppantLbsPerFt: 'proppant_lbs_per_ft',
  fluidGalPerFt: 'fluid_gal_per_ft',
  fluidBblPerFt: 'fluid_bbl_per_ft',
  waterGalPerFt: 'water_gal_per_ft',
  waterBblPerFt: 'water_bbl_per_ft',
  acidGalPerFt: 'acid_gal_per_ft',
  acidBblPerFt: 'acid_bbl_per_ft',
  stageSpacingFt: 'stage_spacing_ft',
};

/**
 * Converts TanStack column filters + global search into query params matching
 * the MVT tile controller's `uwi_filter_params` (snake_case, min_/max_ prefix).
 *
 * @param {ColumnFiltersState} columnFilters - Active column filters from TanStack table.
 * @param {string} globalFilter - Global search string.
 * @param {object} options - Optional extra filters (scenario, missing forecast, early time).
 * @return {string} URL-encoded query parameter string.
 */
export function buildMapTileFilterParams(
  columnFilters: ColumnFiltersState,
  globalFilter: string,
  options?: {
    dcaScenarioId?: number | null;
    wellsMissingForecast?: boolean;
    wellsInEarlyTime?: boolean;
    wellsUpdated?: boolean;
  }
): string {
  const params = new URLSearchParams();

  if (globalFilter.trim()) {
    params.set('text', globalFilter.trim());
  }

  if (options?.dcaScenarioId) {
    params.set('dca_scenario_id', String(options.dcaScenarioId));
  }
  if (options?.wellsMissingForecast) {
    params.set('wells_missing_forecast', 'true');
  }
  if (options?.wellsInEarlyTime) {
    params.set('wells_in_early_time', 'true');
  }
  if (options?.wellsUpdated) {
    params.set('wells_updated', 'true');
  }

  for (const { id, value } of columnFilters) {
    if (value === undefined || value === null || value === '') continue;

    const textParam = TEXT_FILTER_MAP[id];
    if (textParam) {
      const v = typeof value === 'string' ? value.trim() : '';
      if (v) params.set(textParam, v);
      continue;
    }

    const rangeParam = RANGE_FILTER_MAP[id];
    if (rangeParam && Array.isArray(value) && value.length === 2) {
      const [min, max] = value as [unknown, unknown];
      if (min !== null && min !== undefined && min !== '') {
        params.set(`min_${rangeParam}`, String(min));
      }
      if (max !== null && max !== undefined && max !== '') {
        params.set(`max_${rangeParam}`, String(max));
      }
    }
  }

  return params.toString();
}

type BuildParams = {
  workspaceId: number;
  dcaScenarioId?: number | null;
  page: number;
  perPage: number;
  text: string;
  columnFilters: ColumnFiltersState;
  uwis?: string[];
  excludeUwis?: string[];
  wellsMissingForecast?: boolean;
  wellsInEarlyTime?: boolean;
  wellsUpdated?: boolean;
};

function toDateRange(value: unknown): DateRangePayload | undefined {
  if (!Array.isArray(value) || value.length !== 2) return undefined;
  const [start, end] = value as [string | null, string | null];
  if (!start && !end) return undefined;
  const o: DateRangePayload = {};
  if (start) o.start = start;
  if (end) o.end = end;
  return o;
}

function toMinMax(value: unknown): MinMaxPayload | undefined {
  if (!Array.isArray(value) || value.length !== 2) return undefined;
  const [min, max] = value as [number | null, number | null];
  if (min === null && max === null) return undefined;
  const o: MinMaxPayload = {};
  if (min !== null) o.min = min;
  if (max !== null) o.max = max;
  return o;
}

function setTextField(
  payload: DcaWorkspaceWellsQueryPayload,
  key: keyof DcaWorkspaceWellsQueryPayload,
  value: unknown
) {
  if (typeof value === 'string' && value.trim()) {
    (payload as Record<string, unknown>)[key as string] = value.trim();
  }
}

/**
 * Maps TanStack column filters + global text to POST /queries/dca_workspace_wells body.
 * @param {object} params - Workspace id, pagination, search text, column filters; optional dcaScenarioId.
 * @return {Object} POST body for DcaWorkspaceWellsQueryPayload.
 */
export function buildWorkspaceWellsQueryPayload(
  params: BuildParams
): DcaWorkspaceWellsQueryPayload {
  const payload: DcaWorkspaceWellsQueryPayload = {
    dcaWorkspaceId: params.workspaceId,
    page: params.page,
    perPage: params.perPage,
  };

  if (params.dcaScenarioId !== undefined && params.dcaScenarioId !== null) {
    payload.dcaScenarioId = params.dcaScenarioId;
  }

  if (params.text.trim()) {
    payload.text = params.text.trim();
  }

  if (params.uwis?.length) {
    payload.uwis = params.uwis;
  }
  if (params.excludeUwis?.length) {
    payload.excludeUwis = params.excludeUwis;
  }

  if (params.wellsMissingForecast !== undefined) {
    payload.wellsMissingForecast = params.wellsMissingForecast;
  }
  if (params.wellsInEarlyTime !== undefined) {
    payload.wellsInEarlyTime = params.wellsInEarlyTime;
  }
  if (params.wellsUpdated !== undefined) {
    payload.wellsUpdated = params.wellsUpdated;
  }

  for (const f of params.columnFilters) {
    const { id, value } = f;
    if (value === undefined || value === null || value === '') continue;

    switch (id) {
      case 'uwi':
        setTextField(payload, 'uwiText', value);
        break;
      case 'wellName':
        setTextField(payload, 'wellName', value);
        break;
      case 'holeDirection':
        setTextField(payload, 'holeDirection', value);
        break;
      case 'currentOperator':
        setTextField(payload, 'currentOperator', value);
        break;
      case 'spudDate': {
        const r = toDateRange(value);
        if (r) payload.spudDate = r;
        break;
      }
      case 'completionDate': {
        const r = toDateRange(value);
        if (r) payload.completionDate = r;
        break;
      }
      case 'firstProductionDate': {
        const r = toDateRange(value);
        if (r) payload.firstProductionDate = r;
        break;
      }
      case 'totalMonthsOnline': {
        const r = toMinMax(value);
        if (r) payload.totalMonthsOnline = r;
        break;
      }
      case 'trueVerticalDepthFt': {
        const r = toMinMax(value);
        if (r) payload.trueVerticalDepthFt = r;
        break;
      }
      case 'perforatedLateralLengthFt': {
        const r = toMinMax(value);
        if (r) payload.perforatedLateralLengthFt = r;
        break;
      }
      case 'srpLandingZone':
        setTextField(payload, 'srpLandingZone', value);
        break;
      case 'typeCurveFormation':
        setTextField(payload, 'typeCurveFormation', value);
        break;
      case 'typeCurveArea':
        setTextField(payload, 'typeCurveArea', value);
        break;
      case 'typeCurve':
        setTextField(payload, 'typeCurve', value);
        break;
      case 'earlyTimeWell': {
        const v = Array.isArray(value) ? value[0] : value;
        if (v === 'true') payload.earlyTimeWell = true;
        else if (v === 'false') payload.earlyTimeWell = false;
        break;
      }
      case 'oilEurBbl': {
        const r = toMinMax(value);
        if (r) payload.oilEurBbl = r;
        break;
      }
      case 'oilEurPercentile': {
        const r = toMinMax(value);
        if (r) payload.oilEurPercentile = r;
        break;
      }
      case 'oilEurPerFtBblFt': {
        const r = toMinMax(value);
        if (r) payload.oilEurPerFtBblFt = r;
        break;
      }
      case 'oilEurPerFtPercentile': {
        const r = toMinMax(value);
        if (r) payload.oilEurPerFtPercentile = r;
        break;
      }
      case 'gasEurMcf': {
        const r = toMinMax(value);
        if (r) payload.gasEurMcf = r;
        break;
      }
      case 'gasEurPercentile': {
        const r = toMinMax(value);
        if (r) payload.gasEurPercentile = r;
        break;
      }
      case 'gasEurPerFtMcfFt': {
        const r = toMinMax(value);
        if (r) payload.gasEurPerFtMcfFt = r;
        break;
      }
      case 'gasEurPerFtPercentile': {
        const r = toMinMax(value);
        if (r) payload.gasEurPerFtPercentile = r;
        break;
      }
      case 'waterEurBbl': {
        const r = toMinMax(value);
        if (r) payload.waterEurBbl = r;
        break;
      }
      case 'waterEurPercentile': {
        const r = toMinMax(value);
        if (r) payload.waterEurPercentile = r;
        break;
      }
      case 'waterEurPerFtBblFt': {
        const r = toMinMax(value);
        if (r) payload.waterEurPerFtBblFt = r;
        break;
      }
      case 'waterEurPerFtPercentile': {
        const r = toMinMax(value);
        if (r) payload.waterEurPerFtPercentile = r;
        break;
      }
      case 'gorMcfBbl': {
        const r = toMinMax(value);
        if (r) payload.gorMcfBbl = r;
        break;
      }
      case 'gorPercentile': {
        const r = toMinMax(value);
        if (r) payload.gorPercentile = r;
        break;
      }
      case 'cgrBblMmcf': {
        const r = toMinMax(value);
        if (r) payload.cgrBblMmcf = r;
        break;
      }
      case 'cgrPercentile': {
        const r = toMinMax(value);
        if (r) payload.cgrPercentile = r;
        break;
      }
      case 'worBblBbl': {
        const r = toMinMax(value);
        if (r) payload.worBblBbl = r;
        break;
      }
      case 'worPercentile': {
        const r = toMinMax(value);
        if (r) payload.worPercentile = r;
        break;
      }
      case 'wgrBblMmcf': {
        const r = toMinMax(value);
        if (r) payload.wgrBblMmcf = r;
        break;
      }
      case 'wgrPercentile': {
        const r = toMinMax(value);
        if (r) payload.wgrPercentile = r;
        break;
      }
      case 'jobIdDisplay':
        setTextField(payload, 'jobIdText', value);
        break;
      case 'jobDate': {
        const r = toDateRange(value);
        if (r) payload.jobDate = r;
        break;
      }
      case 'proppantLbsPerFt': {
        const r = toMinMax(value);
        if (r) payload.proppantLbsPerFt = r;
        break;
      }
      case 'fluidGalPerFt': {
        const r = toMinMax(value);
        if (r) payload.fluidGalPerFt = r;
        break;
      }
      case 'fluidBblPerFt': {
        const r = toMinMax(value);
        if (r) payload.fluidBblPerFt = r;
        break;
      }
      case 'waterGalPerFt': {
        const r = toMinMax(value);
        if (r) payload.waterGalPerFt = r;
        break;
      }
      case 'waterBblPerFt': {
        const r = toMinMax(value);
        if (r) payload.waterBblPerFt = r;
        break;
      }
      case 'acidGalPerFt': {
        const r = toMinMax(value);
        if (r) payload.acidGalPerFt = r;
        break;
      }
      case 'acidBblPerFt': {
        const r = toMinMax(value);
        if (r) payload.acidBblPerFt = r;
        break;
      }
      case 'stageSpacingFt': {
        const r = toMinMax(value);
        if (r) payload.stageSpacingFt = r;
        break;
      }
      case 'proppantMeshSize':
        setTextField(payload, 'proppantMeshSize', value);
        break;
      case 'fluidSystem':
        setTextField(payload, 'fluidSystem', value);
        break;
      case 'createdAtDisplay': {
        const r = toDateRange(value);
        if (r) payload.createdAt = r;
        break;
      }
      case 'createdByDisplay':
        setTextField(payload, 'createdBy', value);
        break;
      default:
        break;
    }
  }

  return payload;
}

/**
 * Extracts only the table/column filter fields (text search, date ranges,
 * min/max ranges, etc.) from column filters + global search, suitable for
 * nesting under a `filters` key in API payloads.
 *
 * @param {ColumnFiltersState} columnFilters - Active column filters from TanStack table.
 * @param {string} globalFilter - Global search string.
 * @return {Record<string, unknown>} Filter fields record.
 */
export function buildTableFilters(
  columnFilters: ColumnFiltersState,
  globalFilter: string
): Record<string, unknown> {
  if (!columnFilters.length && !globalFilter.trim()) return {};

  const full = buildWorkspaceWellsQueryPayload({
    workspaceId: 0,
    page: 1,
    perPage: 1,
    text: globalFilter,
    columnFilters,
  });

  const filters: Record<string, unknown> = { ...full };
  delete filters.dcaWorkspaceId;
  delete filters.page;
  delete filters.perPage;
  delete filters.dcaScenarioId;
  return filters;
}
